import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, CheckCircle2, Menu, X, Calendar, Clock, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { SEO } from '../components/SEO';
export function BlogPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const {
    country,
    setCountry
  } = useCountry();
  const isRTL = language === 'ar';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
    setLanguageDropdownOpen(false);
    window.location.reload();
  };
  const handleCountryChange = (newCountry: 'eg' | 'ae' | 'sa') => {
    setCountry(newCountry);
    setCountryDropdownOpen(false);
    window.location.reload();
  };
  const posts = [{
    id: 1,
    title: isRTL ? 'كيف تزيد مبيعاتك عبر واتساب؟' : 'How to Increase Sales via WhatsApp?',
    excerpt: isRTL ? 'اكتشف استراتيجيات فعالة لاستخدام واتساب كقناة بيع أساسية وتحويل المحادثات إلى مبيعات حقيقية.' : 'Discover effective strategies to use WhatsApp as a primary sales channel and convert conversations into real sales.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80&w=800',
    date: 'Jan 20, 2026',
    readTime: '5 min read',
    category: isRTL ? 'مبيعات' : 'Sales'
  }, {
    id: 2,
    title: isRTL ? 'مستقبل الذكاء الاصطناعي في خدمة العملاء' : 'The Future of AI in Customer Service',
    excerpt: isRTL ? 'كيف يغير الذكاء الاصطناعي وجه خدمة العملاء؟ تعرف على أحدث الاتجاهات والتقنيات.' : 'How is AI changing the face of customer service? Learn about the latest trends and technologies.',
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80&w=800',
    date: 'Jan 18, 2026',
    readTime: '4 min read',
    category: isRTL ? 'تكنولوجيا' : 'Technology'
  }, {
    id: 3,
    title: isRTL ? 'أتمتة الردود: دليل المبتدئين' : 'Response Automation: Beginners Guide',
    excerpt: isRTL ? 'دليل شامل للبدء في أتمتة ردود واتساب وتوفير الوقت والجهد لفريقك.' : 'A comprehensive guide to start automating WhatsApp responses and saving time and effort for your team.',
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=800',
    date: 'Jan 15, 2026',
    readTime: '6 min read',
    category: isRTL ? 'دليل' : 'Guide'
  }, {
    id: 4,
    title: isRTL ? 'أهمية واتساب API للشركات الكبيرة' : 'Importance of WhatsApp API for Enterprises',
    excerpt: isRTL ? 'لماذا يعتبر واتساب API ضرورياً للشركات التي تتعامل مع حجم كبير من الرسائل؟' : 'Why is WhatsApp API essential for companies dealing with a large volume of messages?',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800',
    date: 'Jan 10, 2026',
    readTime: '7 min read',
    category: isRTL ? 'أعمال' : 'Business'
  }];
  return <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-759">
      <SEO title={isRTL ? 'المدونة - نصائح أتمتة واتساب والذكاء الاصطناعي' : 'Blog - WhatsApp Automation & AI Tips'} description={isRTL ? 'اكتشف أحدث مقالات Operines حول أتمتة واتساب، استراتيجيات المبيعات، والذكاء الاصطناعي في خدمة العملاء.' : 'Discover the latest Operines articles on WhatsApp automation, sales strategies, and AI in customer service.'} keywords={isRTL ? 'مدونة أتمتة, واتساب, ذكاء اصطناعي, مبيعات, خدمة عملاء, نصائح تسويق' : 'automation blog, WhatsApp tips, AI customer service, sales strategies, marketing tips'} canonicalPath="/blog" language={language as 'ar' | 'en'} ogType="article" data-id="element-760" />
      {/* Navigation */}
      <motion.header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm" initial={{
      y: -100
    }} animate={{
      y: 0
    }} data-id="element-761">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-762">
          <div className="flex items-center justify-between py-4" data-id="element-763">
            <Link to="/" data-id="element-764">
              <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-765" />
            </Link>

            <nav className="hidden md:flex items-center gap-8" data-id="element-766">
              <Link to="/" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-767">
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              <Link to="/about" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-768">
                {isRTL ? 'من نحن' : 'About Us'}
              </Link>
              <Link to="/blog" className="text-[#4f1964] font-medium transition-colors" data-id="element-769">
                {isRTL ? 'المدونة' : 'Blog'}
              </Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-770">
                {isRTL ? 'الأسعار' : 'Pricing'}
              </Link>

              {/* Language Selector */}
              <div className="relative" data-id="element-771">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-772">
                  <Globe className="w-5 h-5 text-gray-600" data-id="element-773" />
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-774" />
                </button>
                <AnimatePresence data-id="element-775">
                  {languageDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[140px]" data-id="element-776">
                      <button onClick={() => handleLanguageChange('en')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'en' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-777">
                        <span className="text-lg" data-id="element-778">🇺🇸</span>
                        <span data-id="element-779">English</span>
                        {language === 'en' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-780" />}
                      </button>
                      <button onClick={() => handleLanguageChange('ar')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-781">
                        <span className="text-lg" data-id="element-782">🇸🇦</span>
                        <span data-id="element-783">العربية</span>
                        {language === 'ar' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-784" />}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Country Selector */}
              <div className="relative" data-id="element-785">
                <button onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-786">
                  <span className="text-lg" data-id="element-787">
                    {country === 'eg' ? '🇪🇬' : country === 'ae' ? '🇦🇪' : '🇸🇦'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} data-id="element-788" />
                </button>
                <AnimatePresence data-id="element-789">
                  {countryDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[180px]" data-id="element-790">
                      <button onClick={() => handleCountryChange('eg')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'eg' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-791">
                        <span className="text-2xl" data-id="element-792">🇪🇬</span>
                        <div className="text-left" data-id="element-793">
                          <div className="font-semibold" data-id="element-794">
                            {isRTL ? 'مصر' : 'Egypt'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('ae')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'ae' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-795">
                        <span className="text-2xl" data-id="element-796">🇦🇪</span>
                        <div className="text-left" data-id="element-797">
                          <div className="font-semibold" data-id="element-798">
                            {isRTL ? 'الإمارات' : 'UAE'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('sa')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'sa' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-799">
                        <span className="text-2xl" data-id="element-800">🇸🇦</span>
                        <div className="text-left" data-id="element-801">
                          <div className="font-semibold" data-id="element-802">
                            {isRTL ? 'السعودية' : 'Saudi'}
                          </div>
                        </div>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600 hover:text-[#4f1964]" data-id="element-803">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-804" /> : <Menu className="w-6 h-6" data-id="element-805" />}
            </button>

            <div className="hidden md:flex items-center gap-3" data-id="element-806">
              <Link to="/login" data-id="element-807">
                <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-808">
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Button>
              </Link>
              <Link to="/register" data-id="element-809">
                <Button size="sm" className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-810">
                  {isRTL ? 'ابدأ الآن' : 'Start Now'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-purple-50 to-white" data-id="element-811">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-812">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-813">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-6" data-id="element-814">
              <span className="relative flex h-2 w-2" data-id="element-815">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4f1964] opacity-75" data-id="element-816"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4f1964]" data-id="element-817"></span>
              </span>
              {isRTL ? 'المدونة' : 'Our Blog'}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-818">
              {isRTL ? 'أحدث المقالات والأخبار' : 'Latest Insights & News'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-819">
              {isRTL ? 'اكتشف أحدث استراتيجيات الأتمتة، نصائح واتساب، وأخبار الذكاء الاصطناعي.' : 'Discover the latest automation strategies, WhatsApp tips, and AI news.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-white" data-id="element-820">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-821">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-822">
            {posts.map((post, i) => <motion.article key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-lg hover:shadow-xl transition-all group cursor-pointer" data-id="element-823">
                <div className="relative h-48 overflow-hidden" data-id="element-824">
                  <div className="absolute top-4 left-4 z-10 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-[#4f1964]" data-id="element-825">
                    {post.category}
                  </div>
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-826" />
                </div>
                <div className="p-6" data-id="element-827">
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3" data-id="element-828">
                    <div className="flex items-center gap-1" data-id="element-829">
                      <Calendar className="w-3 h-3" data-id="element-830" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-831">
                      <Clock className="w-3 h-3" data-id="element-832" />
                      {post.readTime}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#4f1964] transition-colors" data-id="element-833">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-834">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-[#4f1964] font-medium text-sm group-hover:gap-2 transition-all" data-id="element-835">
                    {isRTL ? 'اقرأ المزيد' : 'Read More'}
                    {isRTL ? <ArrowLeft className="w-4 h-4 mr-1" data-id="element-836" /> : <ArrowRight className="w-4 h-4 ml-1" data-id="element-837" />}
                  </div>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* Footer (Reused) */}
      <footer className="bg-[#4f1964] text-white py-12" data-id="element-838">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-839">
          <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-840">
            <div data-id="element-841">
              <span className="text-2xl font-bold text-white mb-4 block" data-id="element-842">
                operines
              </span>
              <p className="text-purple-200 text-sm" data-id="element-843">
                {isRTL ? 'أتمتة واتساب بالذكاء الاصطناعي' : 'AI-Powered WhatsApp Automation'}
              </p>
            </div>
            <div data-id="element-844">
              <h4 className="font-bold mb-4" data-id="element-845">
                {isRTL ? 'روابط سريعة' : 'Quick Links'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-846">
                <li data-id="element-847">
                  <Link to="/" className="hover:text-white transition-colors" data-id="element-848">
                    {isRTL ? 'الرئيسية' : 'Home'}
                  </Link>
                </li>
                <li data-id="element-849">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-850">
                    {isRTL ? 'من نحن' : 'About Us'}
                  </Link>
                </li>
                <li data-id="element-851">
                  <Link to="/blog" className="hover:text-white transition-colors" data-id="element-852">
                    {isRTL ? 'المدونة' : 'Blog'}
                  </Link>
                </li>
                <li data-id="element-853">
                  <Link to="/#pricing" className="hover:text-white transition-colors" data-id="element-854">
                    {isRTL ? 'الأسعار' : 'Pricing'}
                  </Link>
                </li>
              </ul>
            </div>
            <div data-id="element-855">
              <h4 className="font-bold mb-4" data-id="element-856">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-857">
                <li data-id="element-858">hello@operines.ai</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-800 pt-8 text-center text-purple-200 text-sm" data-id="element-859">
            © {new Date().getFullYear()} Operines.{' '}
            {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </div>
        </div>
      </footer>
    </div>;
}